import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { api_url } from '../../config';
import axios from 'axios';

import FormHeader from './EditProductForm/FormHeader';
import LoadingSpinner from './EditProductForm/LoadingSpinner';
import ProductDisplay from './EditProductForm/ProductDisplay';
import PriceSelector from './EditProductForm/PriceSelector';
import FormField from './EditProductForm/FormField';
import FormButtons from './EditProductForm/FormButtons';
import StatusMessage from './EditProductForm/StatusMessage';
import validationSchema from './EditProductForm/ValidationSchema';

const EditProductPlan = ({ plan, onClose, fetchPlans, stripeData }) => {
    const { token, id } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState("");
    const [showDiscount, setShowDiscount] = useState(plan?.apply_promo === 1);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            name: plan?.name || '',
            description: plan?.description || '',
            billable: plan?.billable || '',
            app: plan?.app || '',
            features: plan?.features ? plan.features.join(', ') : '',
            users_limit: plan?.users_limit || '',
            branches_limit: plan?.branches_limit || '',
            stripe_price: '',
            price: '',
            apply_promo: plan?.apply_promo === 1 ? "true" : "false",
            discounted_amount: plan?.discounted_amount || '',
            stripe_product: plan?.stripe_product || ''
        }
    });

    useEffect(() => {
        if (plan) {
            reset({
                name: plan.name || '',
                description: plan.description || '',
                billable: plan.billable || '',
                app: plan.app || '',
                features: plan.features ? plan.features.join(', ') : '',
                users_limit: plan.users_limit || '',
                branches_limit: plan.branches_limit || '',
                stripe_price: '',
                price: '',
                apply_promo: plan.apply_promo === 1 ? "true" : "false",
                discounted_amount: plan.discounted_amount || '',
                stripe_product: plan.stripe_product || ''
            });
        }
    }, [plan, reset]);

    const applyPromo = watch("apply_promo");

    useEffect(() => {
        setShowDiscount(applyPromo === "true");
    }, [applyPromo]);

    const handlePriceChange = (e) => {
        const priceId = e.target.value;
        setValue("stripe_price", priceId);

        const selectedPriceObj = stripeData?.find(
            (product) => product.product.id === plan?.stripe_product
        )?.product.prices.find(
            (price) => price.id === priceId
        );
        
        if (selectedPriceObj) {
            setValue("price", selectedPriceObj.unit_amount);
            setValue("billable", selectedPriceObj.recurring.interval);
        }
    };

    const updatePlan = async (data) => {
        try {
            setLoading(true);
            const planData = {
                ...data,
                apply_promo: data.apply_promo === "true",
                discounted_amount: data.apply_promo === "true" ? data.discounted_amount : null,
            };
            
            // Check if token needs to be formatted
            let authToken = token;
            if (token && token.includes('|')) {
                authToken = token.split('|')[1];
            }

            await axios.put(
                `${api_url}/v1/admin/${id}/plans/${plan.id}`,
                planData,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            setStatus("Plan updated successfully");
            await fetchPlans();
            setTimeout(() => {
                setStatus("");
                onClose();
            }, 1500);
            reset();
        } catch (error) {
            setError(error?.response?.data?.error || "An error occurred");
            setTimeout(() => {
                setError("");
            }, 3500);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white p-5 rounded-lg shadow-xl w-full h-full overflow-auto">
            <FormHeader onClose={onClose} />

            <form onSubmit={handleSubmit(updatePlan)} className="space-y-4">
                {loading && <LoadingSpinner />}
                
                <ProductDisplay stripeData={stripeData} plan={plan} />

                <FormField 
                    label="Package Name" 
                    name="name" 
                    register={register} 
                    errors={errors} 
                />

                <FormField 
                    label="App Type" 
                    name="app" 
                    register={register} 
                    errors={errors} 
                    readOnly={true} 
                />

                <PriceSelector 
                    stripeData={stripeData} 
                    plan={plan} 
                    register={register} 
                    handlePriceChange={handlePriceChange} 
                    errors={errors} 
                />

                <input type="hidden" {...register("price")} />
                <input type="hidden" {...register("stripe_product")} />

                <FormField 
                    label="Description" 
                    name="description" 
                    register={register} 
                    errors={errors} 
                    type="textarea" 
                />

                <FormField 
                    label="Billing Period" 
                    name="billable" 
                    register={register} 
                    errors={errors} 
                    type="select" 
                    options={[
                        { value: "monthly", label: "Monthly" },
                        { value: "yearly", label: "Annually" },
                        { value: "semi-annually", label: "Semi-annually" },
                        { value: "quarterly", label: "Quarterly" },
                    ]} 
                />

                <FormField 
                    label="Features" 
                    name="features" 
                    register={register} 
                    errors={errors} 
                    placeholder="Enter features separated by commas" 
                />

                <FormField 
                    label="Apply Promo" 
                    name="apply_promo" 
                    register={register} 
                    errors={errors} 
                    type="select" 
                    options={[
                        { value: "false", label: "No" },
                        { value: "true", label: "Yes" },
                    ]} 
                />

                {showDiscount && (
                    <FormField 
                        label="Discounted Amount" 
                        name="discounted_amount" 
                        register={register} 
                        errors={errors} 
                        type="number" 
                        placeholder="Enter discount amount" 
                    />
                )}

                <FormField 
                    label="No. of branches" 
                    name="branches_limit" 
                    register={register} 
                    errors={errors} 
                    type="number" 
                />

                <FormField 
                    label="No. of Users" 
                    name="users_limit" 
                    register={register} 
                    errors={errors} 
                    type="number" 
                />

                <FormButtons onClose={onClose} loading={loading} />
                <StatusMessage status={status} error={error} />
            </form>
        </div>
    );
};

export default EditProductPlan; 